import type { InfiniteData } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import type { AtomWithInfiniteQueryResult } from 'jotai-tanstack-query';
import { atomWithInfiniteQuery } from 'jotai-tanstack-query';
import { useEffect, useMemo } from 'react';

import {
  activeSfxSimilarityDropTokensModeAtom,
  activeSfxSimilarityFieldOrderAtom,
  activeSfxSimilarityFieldsEnabledAtom,
  activeSfxSimilarityWeightsAtom,
  similarityWeightsEnabledAtom,
} from '@/atoms/similarityWeightsDebug';
import {
  createPageQueueAtom,
  currentPageQueueAtom,
} from '@/features/AudioPlayer/atoms/queue/derivedQueueAtoms';
import {
  FREE_PAGE_SIZE,
  PREMIUM_PAGE_SIZE,
  SINGLE_QUERY_PAGE_SIZE,
  deduplicateSearchHits,
  intersperseAssets,
  processSearchResults,
} from '@music-vine/setlist';
import { similarityKeys } from '@/utils/queryKeys';
import { setlist } from '@/utils/typesense';
import { useSearchInfiniteScroll } from './shared/useSearchInfiniteScroll';
import { useSearchReturn } from './shared/useSearchReturn';

import type {
  DocumentSchema,
  DropTokensMode,
  SearchResponse,
  SearchResponseHit,
  SfxDocument,
  SfxSearchResponse,
  SfxSimilarityField,
  SfxSimilarityFieldsEnabled,
  SfxSimilarityWeights,
  SfxWithEngagementDocument,
  SortOption,
} from '@music-vine/setlist';
import { useSubscriptionType } from '../auth';

// Cache configuration constants (in milliseconds)
const FIVE_MINUTES_MS = 300_000;
const TEN_MINUTES_MS = 600_000;

// Helper function to perform SFX similarity search
const performSfxSimilaritySearch = async ({
  referenceSfx,
  excludeSfxId,
  sort,
  sortScoreVersion,
  fetchFreeAndPremium,
  pageParam = 1,
  isSubscribedUser = false,
  weightOverrides,
  fieldOrderOverride,
  dropTokensModeOverride,
  fieldsEnabledOverride,
  debugEnabled = false,
}: {
  referenceSfx: SfxWithEngagementDocument;
  excludeSfxId?: string;
  sort: SortOption;
  sortScoreVersion: 'v1' | 'v2' | 'v3' | 'v4' | 'v5';
  fetchFreeAndPremium: boolean;
  pageParam?: number;
  isSubscribedUser: boolean;
  weightOverrides?: SfxSimilarityWeights | null;
  fieldOrderOverride?: SfxSimilarityField[] | null;
  dropTokensModeOverride?: DropTokensMode | null;
  fieldsEnabledOverride?: SfxSimilarityFieldsEnabled | null;
  debugEnabled?: boolean;
}) => {
  const response = (await setlist.getSimilarAssets({
    type: 'sfx',
    reference: referenceSfx,
    excludeId: excludeSfxId,
    sort,
    intersperse: fetchFreeAndPremium,
    page: pageParam,
    sortScoreVersion,
    isSubscribed: isSubscribedUser,
    overrides: {
      weights: weightOverrides,
      fieldOrder: fieldOrderOverride,
      dropTokensMode: dropTokensModeOverride,
      fieldsEnabled: fieldsEnabledOverride,
    },
    useCache: !debugEnabled,
  })) as {
    results: SearchResponse<SfxDocument>[];
  };

  return {
    results: response.results,
    page: pageParam,
  } as SfxSearchResponse;
};

/**
 * Custom hook for similar SFX search with audio player queue management
 * Uses the reference SFX's keywords and styles to find similar SFX
 */
export const useSimilarSfxSearch = ({
  referenceSfx,
  excludeSfxId,
  sort = 'relevancy' as SortOption,
  sortScoreVersion = 'v1' as 'v1' | 'v2' | 'v3' | 'v4' | 'v5',
  contextPath = '',
  autoSetupQueue = true,
  fetchFreeAndPremium = true,
  enabled = true,
}: {
  referenceSfx: SfxWithEngagementDocument | null;
  excludeSfxId?: string;
  sort?: SortOption;
  sortScoreVersion?: 'v1' | 'v2' | 'v3' | 'v4' | 'v5';
  contextPath?: string;
  autoSetupQueue?: boolean;
  fetchFreeAndPremium?: boolean;
  enabled?: boolean;
}) => {
  const subscriptionType = useSubscriptionType();
  const isFree = subscriptionType === 'Free';
  const isSubscribedUser = !isFree;

  // Read SFX similarity debug overrides from atoms (null if not enabled)
  const debugEnabled = useAtomValue(similarityWeightsEnabledAtom);
  const weightOverrides = useAtomValue(activeSfxSimilarityWeightsAtom);
  const fieldOrderOverride = useAtomValue(activeSfxSimilarityFieldOrderAtom);
  const dropTokensModeOverride = useAtomValue(
    activeSfxSimilarityDropTokensModeAtom
  );
  const fieldsEnabledOverride = useAtomValue(
    activeSfxSimilarityFieldsEnabledAtom
  );

  // Normalize fetch parameters based on subscription
  // Only intersperse free/premium for free users with relevancy sort
  const normalizedFetchFreeAndPremium =
    sort === 'relevancy' && isFree ? fetchFreeAndPremium : false;

  // Create stable query key using centralized builder to ensure SSR/client sync
  const queryKey = useMemo(
    () =>
      similarityKeys.sfx({
        referenceSfxId: referenceSfx?.sfx_id ?? '',
        excludeSfxId: excludeSfxId ?? referenceSfx?.sfx_id ?? '',
        sort,
        sortScoreVersion,
        fetchFreeAndPremium: normalizedFetchFreeAndPremium,
        isSubscribedUser,
        // Include debug overrides in query key so search refreshes when they change
        weightOverrides: weightOverrides
          ? JSON.stringify(weightOverrides)
          : null,
        fieldOrderOverride: fieldOrderOverride
          ? JSON.stringify(fieldOrderOverride)
          : null,
        dropTokensModeOverride: dropTokensModeOverride ?? null,
        fieldsEnabledOverride: fieldsEnabledOverride
          ? JSON.stringify(fieldsEnabledOverride)
          : null,
      }),
    [
      referenceSfx?.sfx_id,
      excludeSfxId,
      sort,
      sortScoreVersion,
      normalizedFetchFreeAndPremium,
      isSubscribedUser,
      weightOverrides,
      fieldOrderOverride,
      dropTokensModeOverride,
      fieldsEnabledOverride,
    ]
  );

  // Create atom with stable parameters
  const searchAtom = useMemo(() => {
    return atomWithInfiniteQuery(() => ({
      queryKey,
      queryFn: ({ pageParam = 1 }: { pageParam?: number }) => {
        if (!referenceSfx) {
          throw new Error('Reference SFX is required');
        }
        return performSfxSimilaritySearch({
          referenceSfx,
          excludeSfxId,
          sort,
          sortScoreVersion,
          fetchFreeAndPremium: normalizedFetchFreeAndPremium,
          pageParam,
          isSubscribedUser,
          weightOverrides,
          fieldOrderOverride,
          dropTokensModeOverride,
          fieldsEnabledOverride,
          debugEnabled,
        });
      },
      getNextPageParam: (lastPage: SfxSearchResponse) => {
        if (!Array.isArray(lastPage.results) || lastPage.results.length === 0) {
          return;
        }

        // Check if we have full pages to determine if there are more results
        const hasMoreResults = lastPage.results.some((result) => {
          if (!result.hits || result.hits.length === 0) {
            return false;
          }

          if (normalizedFetchFreeAndPremium) {
            return (
              result.hits.length >= PREMIUM_PAGE_SIZE ||
              result.hits.length >= FREE_PAGE_SIZE
            );
          }

          return result.hits.length >= SINGLE_QUERY_PAGE_SIZE;
        });

        return hasMoreResults ? lastPage.page + 1 : undefined;
      },
      initialPageParam: 1,
      enabled: enabled && !!referenceSfx,
      staleTime: FIVE_MINUTES_MS,
      gcTime: TEN_MINUTES_MS,
    }));
  }, [
    queryKey,
    referenceSfx,
    excludeSfxId,
    sort,
    sortScoreVersion,
    normalizedFetchFreeAndPremium,
    isSubscribedUser,
    weightOverrides,
    fieldOrderOverride,
    dropTokensModeOverride,
    fieldsEnabledOverride,
    debugEnabled,
    enabled,
  ]);

  // Get search result from the atom
  const searchResult = useAtomValue(searchAtom);

  // Process results with interspersing logic
  const results = useMemo<SearchResponseHit<SfxDocument>[]>(() => {
    if (!searchResult.data) {
      return [];
    }

    const allResults = searchResult.data.pages.flatMap((page: unknown) => {
      const sfxSearchPage = page as SfxSearchResponse;
      const searchResults = sfxSearchPage.results;

      if (!Array.isArray(searchResults)) {
        return [];
      }

      // If fetchFreeAndPremium is true and we have 2 result sets, intersperse them
      const premiumResults = searchResults.at(0);
      const freeResults = searchResults.at(1);

      if (
        normalizedFetchFreeAndPremium &&
        searchResults.length === 2 &&
        premiumResults &&
        freeResults
      ) {
        return intersperseAssets({
          premiumAssetSearchResults: premiumResults,
          freeAssetSearchResults: freeResults,
        });
      }

      // Otherwise return the hits from the first result
      return searchResults.at(0)?.hits ?? [];
    });

    return deduplicateSearchHits(allResults, (doc) => doc.sfx_variant_id);
  }, [searchResult.data, normalizedFetchFreeAndPremium]);

  // Context for the queue
  const queueContext = useMemo(
    () => ({
      source: contextPath || 'similar-sfx-search',
      sourceType: 'search' as const,
      sourceName: referenceSfx?.name
        ? `Similar to: ${referenceSfx.name}`
        : 'Similar SFX',
      sourceId: referenceSfx?.sfx_id ?? undefined,
    }),
    [contextPath, referenceSfx?.name, referenceSfx?.sfx_id]
  );

  // Create derived queue atom that automatically updates with search results
  const pageQueueAtom = useMemo(() => {
    if (!autoSetupQueue) {
      return null;
    }

    // Create a processing function that replicates the interspersing logic
    const processResults = (
      atomResult: AtomWithInfiniteQueryResult<
        InfiniteData<SfxSearchResponse>,
        Error
      >
    ) => {
      if (!atomResult?.data) {
        return [];
      }

      const flatResults = atomResult.data.pages.flatMap(
        (page: SfxSearchResponse) => {
          const searchResults = page.results;

          if (!Array.isArray(searchResults)) {
            return [];
          }

          // Use shared utility for processing search results
          return processSearchResults(
            searchResults,
            normalizedFetchFreeAndPremium
          ) as unknown as SearchResponseHit<DocumentSchema>[];
        }
      );

      return deduplicateSearchHits(
        flatResults,
        (doc) => (doc as unknown as SfxDocument).sfx_variant_id
      );
    };

    return createPageQueueAtom(searchAtom, queueContext, processResults);
  }, [searchAtom, queueContext, autoSetupQueue, normalizedFetchFreeAndPremium]);

  // Set global page queue atom for Track components to access
  const setCurrentPageQueue = useSetAtom(currentPageQueueAtom);

  useEffect(() => {
    setCurrentPageQueue(pageQueueAtom);
    // No cleanup needed - let the next page set its own queue
  }, [pageQueueAtom, setCurrentPageQueue]);

  // Use shared infinite scroll logic
  const { loadMoreRef, fetchNextPage } = useSearchInfiniteScroll({
    hasNextPage: searchResult.hasNextPage,
    isFetchingNextPage: searchResult.isFetchingNextPage,
    fetchNextPage: async () => {
      await searchResult.fetchNextPage();
    },
  });

  // Use shared return logic
  return useSearchReturn({
    results,
    searchResult,
    isLoading: searchResult.isLoading && !searchResult.data,
    error: searchResult.error,
    loadMoreRef,
    fetchNextPage,
  });
};
